import { useState, useRef, useEffect, ChangeEvent, FormEvent } from "react";
import { ApplicationNavbar } from "@/ui/components/navigation";
import { ApplicationMenuModal } from "@/ui/components/common";
import {
  ConversationMain,
  ConversationBottomNav,
  ConversationLoadingModal,
} from "@/ui/components/chat";
import { MessageUI, QuickActionUI } from "@/ui/components/chat/types";
import { useChatService } from "@/application/services/chatService";
import { usePageService } from "@/application/services/pageService";

/**
 * 對話式建站頁面
 * 顯示與 AI 助手的對話介面
 */
export function ConversationPage() {
  const { getPageMessages, sendMessage } = useChatService;
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [messages, setMessages] = useState<MessageUI[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // 快速操作選項
  const [quickActions, setQuickActions] = useState<QuickActionUI[]>([]);

  useEffect(() => {
    const getUserContext = async () => {
      const context = await usePageService.getUserContext();
      if (!context.current_project_id || !context.current_page_id) return;
      const page = await usePageService.getPageById(
        context.current_project_id,
        context.current_page_id,
      );
      setSessionId(page.page_conversation_id);
      setIsLoading(true);
    };

    getUserContext();
  }, []);

  useEffect(() => {
    if (isLoading) {
      const t = setInterval(async () => {
        if (!sessionId) return [];
        const pageMessages = await getPageMessages(sessionId);
        if (pageMessages.length === 0) return [];
        const lastMessage = pageMessages[pageMessages.length - 1];

        const diff =
          new Date().getTime() -
          new Date(lastMessage.created_at).getTime() +
          new Date().getTimezoneOffset() * 60 * 1000;

        if (diff >= 10 * 60 * 1000) {
          setIsLoading(false);
        } else if (lastMessage.role !== "user") {
          try {
            const [_, meta] = formatContent(lastMessage.content);
            const action_slug = meta?.action_slug;
            const options = meta?.options || [];
            setQuickActions(
              options.map((option: string) => ({
                id: option,
                text: option,
                icon: "",
              })),
            );

            if (!action_slug || action_slug !== "cta_wait_build") {
              setIsLoading(false);
            }
          } catch (error) {
            setQuickActions([]);
            setIsLoading(false);
          }
        }
        const formattedMessages: MessageUI[] = pageMessages
          .filter(
            (message, index) =>
              index === 0 || message.id !== pageMessages[index - 1].id,
          )
          .map((message, index) => ({
            id: message.id,
            content: formatContent(message.content)[0],
            metadata: formatContent(message.content)[1],
            sender: message.role === "user" ? "user" : "ai",
            timestamp: new Date(message.created_at),
            lastMessage: index === messages.length - 1,
          }));

        const messagesIds = messages.map((message) => message.id);
        const newMessages: MessageUI[] = formattedMessages.filter(
          (message) => !messagesIds.includes(message.id),
        );
        if (newMessages.length > 0) {
          setMessages(formattedMessages);
        }
      }, 1000);
      return () => clearInterval(t);
    }
  }, [isLoading, sessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const formatContent = (
    message: string,
  ): [
    content: string,
    meta: { options?: string[]; [key: string]: unknown },
  ] => {
    const splits = message.split("\nmeta: ");
    const meta = splits[1]?.trim();
    const metaJson = meta ? JSON.parse(meta) : {};
    return [splits[0].trim(), metaJson];
  };

  // 自動調整文字輸入框高度
  const autoResize = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        128,
      )}px`;
    }
  };

  // 處理輸入變更
  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.currentTarget.value);
    autoResize();
  };

  // 處理按鍵事件
  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e as unknown as FormEvent);
    }
  };

  // 處理訊息發送
  const handleSendMessage = async (e?: FormEvent, messageText?: string) => {
    if (e) e.preventDefault();
    const textToSend = messageText !== undefined ? messageText : inputValue;
    if (!textToSend.trim() || isLoading) return;
    if (!sessionId) return;

    // 添加用戶訊息
    const userMessage: MessageUI = {
      id: Date.now().toString(),
      content: textToSend.trim(),
      sender: "user",
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    // 自動調整文字輸入框高度
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
    await sendMessage(sessionId, textToSend).then(() => {
      setIsLoading(true);
    });
  };

  // 處理快速操作點擊
  const handleQuickActionClick = (action: QuickActionUI) => {
    setInputValue(action.text);
    if (textareaRef.current) {
      textareaRef.current.focus();
      autoResize();
    }
    setQuickActions([]);
    handleSendMessage(undefined, action.text);
  };

  // 切換選單
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <>
      <ApplicationNavbar onMenuClick={toggleMenu} />
      <ConversationMain messages={messages} messagesEndRef={messagesEndRef} />
      <ConversationLoadingModal isLoading={isLoading} />
      <ConversationBottomNav
        quickActions={quickActions}
        textareaRef={textareaRef}
        isLoading={isLoading}
        inputValue={inputValue}
        handleInputChange={handleInputChange}
        handleKeyPress={handleKeyPress}
        handleSendMessage={handleSendMessage}
        handleQuickActionClick={handleQuickActionClick}
      />

      <ApplicationMenuModal
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={toggleMenu}
      />
    </>
  );
}
